import React, { useEffect } from 'react';
import { Modal, Form, Input } from 'antd';

import { DevPlanAdmin } from 'state';

export type CreatePlanValues = {
  code: string;
  name: string;
  description: string;
};

type Props = {
  open: boolean;
  mode: 'create' | 'clone';
  source?: DevPlanAdmin | null;
  onClose: () => void;
  onSubmit: (values: CreatePlanValues) => void;
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/** Create a blank draft or clone an existing tree. `code` is the immutable slug. */
const CreatePlanModal: React.FC<Props> = ({ open, mode, source, onClose, onSubmit }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    if (mode === 'clone' && source) {
      form.setFieldsValue({
        name: `${source.name} (copy)`,
        code: `${source.code}-v${source.version + 1}`,
        description: source.description,
      });
    } else {
      form.resetFields();
    }
  }, [open, mode, source, form]);

  return (
    <Modal
      title={mode === 'clone' ? `Clone "${source?.name ?? ''}"` : 'New development plan'}
      open={open}
      onCancel={onClose}
      okText={mode === 'clone' ? 'Clone' : 'Create'}
      onOk={() =>
        form.validateFields().then((v) => {
          onSubmit({
            code: v.code,
            name: v.name,
            description: v.description || '',
          });
        })
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'required' }]}
        >
          <Input
            onChange={(e) => {
              if (mode === 'create' && !form.isFieldTouched('code')) {
                form.setFieldValue('code', slugify(e.target.value));
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label="Code (slug)"
          name="code"
          extra="URL identity + trainee saved-progress key. Immutable after creation."
          rules={[
            { required: true, message: 'required' },
            {
              pattern: /^[a-z0-9-]+$/,
              message: 'lowercase letters, numbers and hyphens only',
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreatePlanModal;
